import * as React from 'react';
import { InputHTMLAttributes } from 'react';
import { UseFormRegister } from 'react-hook-form';
import { FieldErrors } from 'react-hook-form';
import * as z from 'zod';
import {
  eventsSchema,
  participantsAndGamesSchema,
  whenAndWhereSchema,
} from 'screens/Tournaments/QuickCreatePage/types';
import FieldWrapper from 'screens/TournamentsBuilder/components/FieldWrapper';
import { basicsSchema, eventGroupSchema } from 'screens/TournamentsBuilder/types';
import classNames from 'styles/utils/classNames';

type BasicsFormSchema = z.infer<typeof basicsSchema>;
type CompetitionFormSchema = z.infer<typeof eventGroupSchema>;
type QuickCreateEventFormSchema = z.infer<typeof eventsSchema>;
type ParticipantAndGamesFormSchema = z.infer<typeof participantsAndGamesSchema>;
type WhereFormSchema = z.infer<typeof whenAndWhereSchema>;

export enum InputType {
  Regular = 'regular',
  RegularWithIcon = 'regularIcon',
  Textarea = 'textarea',
  LabelInput = 'label',
  RegularWithoutLabel = 'regularOutLabel',
  TextAreaWithoutLabel = 'textareaWithOutLabel',
}

type Props = {
  name: keyof CompetitionFormSchema | keyof BasicsFormSchema | any;
  register:
    | UseFormRegister<CompetitionFormSchema>
    | UseFormRegister<BasicsFormSchema>
    | UseFormRegister<QuickCreateEventFormSchema>
    | UseFormRegister<ParticipantAndGamesFormSchema>
    | UseFormRegister<WhereFormSchema>;
  errors: FieldErrors<CompetitionFormSchema> | FieldErrors<BasicsFormSchema>;
  placeholder: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;

  fieldLabel?: string;
  fieldDesc?: string;

  inputType: InputType;

  renderIcon?(): React.ReactNode;

  isDisabled?: boolean;
  addStyles?: string;
};

export default function InputField(props: Props) {
  const {
    register,
    renderIcon,
    inputType,
    fieldLabel,
    fieldDesc,
    name,
    inputProps,
    placeholder,
    errors,
    isDisabled,
    addStyles,
  } = props;

  const inputNativeType = inputProps?.type;

  const buildRegister = (register: UseFormRegister<any>) => {
    return register(name as string, {
      valueAsNumber: inputNativeType === 'number',
    });
  };

  if (inputType === InputType.Regular) {
    return (
      <FieldWrapper label={fieldLabel}>
        <input
          {...buildRegister(register)}
          {...(inputProps || {})}
          placeholder={placeholder}
          className="text-color-text-lightmode-placeholder::placeholder dark:text-color-text-darkmode-placeholder::placeholder h-11 w-full rounded-md border-0 bg-brand-gray-50 pl-3 pr-3 font-light text-color-text-lightmode-primary focus:outline-0 dark:text-color-text-darkmode-primary"
          disabled={isDisabled}
        />
        <p className="bottom-1 mt-1 text-[0.75rem] text-color-text-brand dark:text-color-text-brand">
          {/* @ts-ignore */}
          {errors?.[name]?.message as any}
        </p>
      </FieldWrapper>
    );
  }

  if (inputType === InputType.Textarea) {
    return (
      <FieldWrapper label={fieldLabel}>
        <textarea
          {...buildRegister(register)}
          placeholder={placeholder}
          disabled={isDisabled}
          className="text-color-text-lightmode-placeholder::placeholder dark:text-color-text-darkmode-placeholder::placeholder h-20 w-full rounded-md border-0 bg-brand-gray-50 pl-3 pr-3 font-light text-color-text-lightmode-primary focus:outline-0 dark:text-color-text-darkmode-primary"
        />
        <p className="bottom-1 mt-1 text-[0.75rem] text-color-text-brand dark:text-color-text-brand">
          {/* @ts-ignore */}
          {errors?.[name]?.message as any}
        </p>
      </FieldWrapper>
    );
  }

  if (inputType === InputType.LabelInput) {
    return (
      <div className="flex flex-col">
        <span className="mb-2 font-bold text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {fieldLabel}
        </span>

        <input
          {...buildRegister(register)}
          {...(inputProps || {})}
          disabled={isDisabled}
          placeholder={placeholder}
          className="text-color-text-lightmode-placeholder::placeholder dark:text-color-text-darkmode-placeholder::placeholder h-11 w-full rounded-md border-0 bg-brand-gray-50 pl-3 pr-3 font-light text-color-text-lightmode-primary focus:outline-0 dark:text-color-text-darkmode-primary"
        />
        <p className="bottom-1 mt-1 text-[0.75rem] text-color-text-brand dark:text-color-text-brand">
          {/* @ts-ignore */}
          {errors?.[name]?.message as any}
        </p>
      </div>
    );
  }

  if (inputType === InputType.RegularWithIcon) {
    return (
      <div className="flex flex-col">
        {fieldLabel && (
          <span className="mb-2 font-bold text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            {fieldLabel}
          </span>
        )}

        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 transform">
            {renderIcon && renderIcon()}
          </div>
          <input
            {...buildRegister(register)}
            {...(inputProps || {})}
            disabled={isDisabled}
            placeholder={placeholder}
            className={classNames(
              'text-color-text-lightmode-placeholder::placeholder dark:text-color-text-darkmode-placeholder::placeholder h-11 w-full rounded-md border-0 bg-brand-gray-50 pl-7 pr-3 font-light text-color-text-lightmode-primary focus:outline-0 dark:text-color-text-darkmode-primary',
              isDisabled ? 'opacity-50' : 'opacity-100',
            )}
          />
        </div>
        <p className="bottom-1 mt-1 text-[0.75rem] text-color-text-brand dark:text-color-text-brand">
          {/* @ts-ignore */}
          {errors?.[name]?.message as any}
        </p>
      </div>
    );
  }

  if (inputType === InputType.RegularWithoutLabel) {
    return (
      <div className="mt-1">
        <input
          {...buildRegister(register)}
          {...(inputProps || {})}
          placeholder={placeholder}
          className={classNames(
            'text-color-text-lightmode-placeholder::placeholder dark:text-color-text-darkmode-placeholder::placeholder dark:text-color-text-darkmode-primary" h-11 w-full rounded-md border-0 bg-brand-gray-50 pl-3 pr-3 font-light text-color-text-lightmode-primary focus:outline-0',
            addStyles,
          )}
          disabled={isDisabled}
        />
        <p className="bottom-1 mt-1 text-[0.75rem] text-color-text-brand dark:text-color-text-brand">
          {/* @ts-ignore */}
          {errors?.[name]?.message as any}
        </p>
      </div>
    );
  }

  if (inputType === InputType.TextAreaWithoutLabel) {
    return (
      <div className="mt-1">
        <textarea
          {...buildRegister(register)}
          placeholder={placeholder}
          disabled={isDisabled}
          className="text-color-text-lightmode-placeholder::placeholder dark:text-color-text-darkmode-placeholder::placeholder h-20 w-full rounded-md border-0 bg-brand-gray-50 pl-3 pr-3 font-light text-color-text-lightmode-primary focus:outline-0 dark:text-color-text-darkmode-primary"
        />
        <p className="bottom-1 mt-1 text-[0.75rem] text-color-text-brand dark:text-color-text-brand">
          {/* @ts-ignore */}
          {errors?.[name]?.message as any}
        </p>
      </div>
    );
  }
}
